import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Tooltip, createFilterOptions } from '@mui/material';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import { isValidString } from '../../common/CommonUtilsMethods';

/**
 * Contract for a generic, searchable, reusable select.
 * Implementation should stay controlled: callers own `value` / `onChange`; keep internal state
 * limited to open/search UI unless unavoidable.
 */

/** Defaults for selection limits when omitted (`maxSelections` 1 ⇒ at most one item in `value`). */
const WORKY_SELECT_DEFAULT_MIN_SELECTIONS: number = 1;
const WORKY_SELECT_DEFAULT_MAX_SELECTIONS: number = 1;
const WORKY_SELECT_DEFAULT_POPPER_Z_INDEX: number = 1400;

/**
 * Caller-controlled styling hooks. Implementation maps these to DOM or design-system props.
 */
interface WorkySelectStylingOptions {
  readonly rootClassName?: string;
  readonly inputClassName?: string;
  readonly menuClassName?: string;
  readonly optionClassName?: string;
  readonly optionImageClassName?: string;
  readonly placeholderClassName?: string;
  readonly popperClassName?: string;
}

/**
 * Optional text used when filtering options. Defaults to `getLabel` in implementation.
 */
type WorkySelectGetSearchText<T> = (option: T) => string;

interface WorkySelectSharedProps<T> {
  readonly options: readonly T[];
  readonly getLabel: (option: T) => string;
  readonly getValue: (option: T) => string;
  /**
   * Shown when no selection; may be null to omit placeholder copy (implementation-defined).
   */
  readonly placeholder?: string | null;
  readonly disabled?: boolean;
  /**
   * When true (recommended default in implementation), user can type to filter options.
   */
  readonly enableSearch?: boolean;
  readonly getSearchText?: WorkySelectGetSearchText<T>;
  readonly styling?: WorkySelectStylingOptions;
  /**
   * Optional image URL shown beside the label for an option; null/undefined = no image.
   */
  readonly getOptionImageUrl?: (option: T) => string | null | undefined;
  /**
   * Tooltip content when hovering an option; null/undefined = no tooltip.
   */
  readonly getOptionTooltip?: (option: T) => string | null | undefined;
}

/**
 * `value` is always an array: use `[]` when empty; for “single” mode set `maxSelections` to 1
 * (length 0 or 1).
 */
interface WorkySelectComponentProps<T> extends WorkySelectSharedProps<T> {
  readonly minSelections?: number;
  readonly maxSelections?: number;
  /**
   * Stacking order of the options menu. Raise this when the select sits inside an overlay
   * whose z-index is at or above the default.
   */
  readonly popperZIndex?: number;
  readonly value: readonly T[];
  readonly onChange: (nextValue: readonly T[]) => void;
}

const mergeClassNames = (
  a: string | undefined,
  b: string | undefined
): string | undefined => {
  const parts: readonly string[] = [a, b].filter(
    (c): c is string => typeof c === 'string' && c.trim() !== ''
  );
  if (parts.length === 0) {
    return undefined;
  }
  return parts.join(' ');
};

const buildSearchFilter = <T,>(
  getSearchText: WorkySelectGetSearchText<T>
): ((options: readonly T[], state: FilterOptionsState<T>) => T[]) => {
  return createFilterOptions<T>({
    stringify: (option: T): string => getSearchText(option),
    matchFrom: 'any',
    ignoreCase: true,
  });
};

const passthroughFilter = <T,>(options: readonly T[], _state: FilterOptionsState<T>): T[] => {
  return [...options];
};

const renderWorkySelectOption = <T,>(
  listItemProps: React.HTMLAttributes<HTMLLIElement>,
  option: T,
  getLabel: (opt: T) => string,
  getOptionImageUrl: WorkySelectSharedProps<T>['getOptionImageUrl'],
  getOptionTooltip: WorkySelectSharedProps<T>['getOptionTooltip'],
  optionClassName: string | undefined,
  optionImageClassName: string | undefined
): React.ReactElement => {
  const labelText: string = getLabel(option);
  const imageRaw: string | null | undefined = getOptionImageUrl?.(option);
  const tooltipRaw: string | null | undefined = getOptionTooltip?.(option);
  const imageSrc: string | undefined = isValidString(imageRaw) ? imageRaw : undefined;
  const tooltipTitle: string | undefined = isValidString(tooltipRaw) ? tooltipRaw : undefined;

  const row: React.ReactElement = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {imageSrc !== undefined ? (
        <Box
          component="img"
          src={imageSrc}
          alt=""
          className={optionImageClassName}
          sx={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0 }}
        />
      ) : null}
      <span>{labelText}</span>
    </Box>
  );

  const liClassName: string | undefined = mergeClassNames(listItemProps.className, optionClassName);

  const inner: React.ReactElement =
    tooltipTitle !== undefined ? (
      <Tooltip title={tooltipTitle} placement="right">
        <Box component="span" sx={{ display: 'block', width: '100%' }}>
          {row}
        </Box>
      </Tooltip>
    ) : (
      row
    );

  return (
    <li {...listItemProps} className={liClassName}>
      {inner}
    </li>
  );
};

const renderWorkySelectMultiAutocomplete = <T,>(
  filterOptions: (opts: readonly T[], state: FilterOptionsState<T>) => T[],
  styling: WorkySelectStylingOptions | undefined,
  disabled: boolean,
  options: readonly T[],
  value: readonly T[],
  getLabel: (option: T) => string,
  getValue: (option: T) => string,
  getOptionImageUrl: WorkySelectSharedProps<T>['getOptionImageUrl'],
  getOptionTooltip: WorkySelectSharedProps<T>['getOptionTooltip'],
  placeholderText: string | undefined,
  inputReadOnly: boolean,
  applySelectionLimits: (nextValue: readonly T[]) => boolean,
  onChange: (nextValue: readonly T[]) => void,
  popperZIndex: number
): React.ReactElement => {
  const handleMultiChange = (_: React.SyntheticEvent, newValue: readonly T[]): void => {
    if (!applySelectionLimits(newValue)) {
      return;
    }
    onChange(newValue);
  };

  return (
    <Autocomplete<T, true, false, false>
      className={styling?.rootClassName}
      disabled={disabled}
      multiple
      options={[...options]}
      value={[...value]}
      onChange={handleMultiChange}
      getOptionLabel={(option: T): string => getLabel(option)}
      isOptionEqualToValue={(a: T, b: T): boolean => getValue(a) === getValue(b)}
      filterOptions={filterOptions}
      ListboxProps={{ className: styling?.menuClassName }}
      slotProps={{
        popper: {
          className: styling?.popperClassName,
          sx: { zIndex: popperZIndex },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholderText}
          size="small"
          fullWidth
          InputProps={{
            ...params.InputProps,
            readOnly: inputReadOnly,
            className: mergeClassNames(params.InputProps.className as string | undefined, styling?.inputClassName),
          }}
          inputProps={{
            ...params.inputProps,
            className: mergeClassNames(
              params.inputProps.className as string | undefined,
              styling?.placeholderClassName
            ),
          }}
        />
      )}
      renderOption={(listItemProps, option) =>
        renderWorkySelectOption(
          listItemProps,
          option,
          getLabel,
          getOptionImageUrl,
          getOptionTooltip,
          styling?.optionClassName,
          styling?.optionImageClassName
        )
      }
    />
  );
};

const WorkySelectComponent = <T,>(props: WorkySelectComponentProps<T>): React.ReactElement => {
  const {
    options,
    getLabel,
    getValue,
    placeholder,
    disabled = false,
    enableSearch = true,
    getSearchText,
    styling,
    getOptionImageUrl,
    getOptionTooltip,
    value,
    minSelections,
    maxSelections,
    popperZIndex,
    onChange,
  } = props;

  const minSel: number = minSelections ?? WORKY_SELECT_DEFAULT_MIN_SELECTIONS;
  const resolvedPopperZIndex: number = popperZIndex ?? WORKY_SELECT_DEFAULT_POPPER_Z_INDEX;
  const maxSel: number = maxSelections ?? WORKY_SELECT_DEFAULT_MAX_SELECTIONS;
  const isMultiSelect: boolean = maxSel > 1;

  const filterOptions: (opts: readonly T[], state: FilterOptionsState<T>) => T[] = useMemo(() => {
    if (!enableSearch) {
      return passthroughFilter;
    }
    const stringify: WorkySelectGetSearchText<T> = (option: T): string => {
      return getSearchText !== undefined ? getSearchText(option) : getLabel(option);
    };
    return buildSearchFilter(stringify);
  }, [enableSearch, getSearchText, getLabel]);

  const placeholderText: string | undefined =
    placeholder === null || placeholder === undefined ? undefined : placeholder;

  const inputReadOnly: boolean = !enableSearch;

  const applySelectionLimits = (nextValue: readonly T[]): boolean => {
    if (nextValue.length > maxSel) {
      return false;
    }
    const prevLen: number = value.length;
    const nextLen: number = nextValue.length;
    if (nextLen < minSel && nextLen < prevLen) {
      return false;
    }
    return true;
  };

  if (isMultiSelect) {
    return renderWorkySelectMultiAutocomplete(
      filterOptions,
      styling,
      disabled,
      options,
      value,
      getLabel,
      getValue,
      getOptionImageUrl,
      getOptionTooltip,
      placeholderText,
      inputReadOnly,
      applySelectionLimits,
      onChange,
      resolvedPopperZIndex
    );
  }

  const firstSelected: T | undefined = value[0];
  const selectedSingle: T | null = firstSelected !== undefined ? firstSelected : null;

  const handleSingleChange = (_: React.SyntheticEvent, newValue: T | null): void => {
    const nextArr: readonly T[] = newValue !== null ? [newValue] : [];
    if (!applySelectionLimits(nextArr)) {
      return;
    }
    onChange(nextArr);
  };

  return (
    <Autocomplete<T, false, false, false>
      className={styling?.rootClassName}
      disabled={disabled}
      options={[...options]}
      value={selectedSingle}
      onChange={handleSingleChange}
      getOptionLabel={(option: T): string => getLabel(option)}
      isOptionEqualToValue={(a: T, b: T): boolean => getValue(a) === getValue(b)}
      filterOptions={filterOptions}
      ListboxProps={{ className: styling?.menuClassName }}
      slotProps={{
        popper: {
          className: styling?.popperClassName,
          sx: { zIndex: resolvedPopperZIndex },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholderText}
          size="small"
          fullWidth
          InputProps={{
            ...params.InputProps,
            readOnly: inputReadOnly,
            className: mergeClassNames(params.InputProps.className as string | undefined, styling?.inputClassName),
          }}
          inputProps={{
            ...params.inputProps,
            className: mergeClassNames(
              params.inputProps.className as string | undefined,
              styling?.placeholderClassName
            ),
          }}
        />
      )}
      renderOption={(listItemProps, option) =>
        renderWorkySelectOption(
          listItemProps,
          option,
          getLabel,
          getOptionImageUrl,
          getOptionTooltip,
          styling?.optionClassName,
          styling?.optionImageClassName
        )
      }
    />
  );
};

export {
  WORKY_SELECT_DEFAULT_MIN_SELECTIONS,
  WORKY_SELECT_DEFAULT_MAX_SELECTIONS,
  WorkySelectComponent,
};

export type {
  WorkySelectComponentProps,
  WorkySelectGetSearchText,
  WorkySelectSharedProps,
  WorkySelectStylingOptions,
};
